/**
 * Управление часами: пауза/продолжить, скорость ×1…×16 и «до следующего
 * решения» (перемотка на 16× до первого ожидающего решения — часы сами
 * возвращают прежнюю скорость). Все кнопки — обычные `.btn` 44px;
 * скорости — моно, активная помечена `aria-pressed`.
 *
 * `compact` (телефон, ≤760px): одна строка — пауза и текущая ×N; тап по ×N
 * раскрывает остальные скорости и перемотку, выбор сворачивает обратно.
 * `pending` (решение ждёт ответа): часы остановил экран, а не игрок, поэтому
 * кнопка паузы не переименовывается в «Продолжить», а гаснет, и рядом
 * подпись «Ждём решения».
 */
import { t } from '@life/i18n';
import { useState } from 'preact/hooks';
import { SPEEDS, type Clock } from '../state/clock';

export interface SpeedControlProps {
  clock: Clock;
  compact?: boolean;
  pending?: boolean;
}

export function SpeedControl({ clock, compact = false, pending = false }: SpeedControlProps) {
  const [open, setOpen] = useState(false);
  const paused = clock.paused.value;
  const speed = clock.speed.value;
  const expanded = !compact || open;
  return (
    <div class="speed" role="group" aria-label={t('ui.speed')}>
      <button
        type="button"
        class="btn ghost"
        disabled={pending}
        onClick={() => (paused ? clock.resume() : clock.pause())}
      >
        {t(paused && !pending ? 'ui.life.resume' : 'ui.life.pause')}
      </button>
      {pending && <span class="chip">{t('ui.life.waiting')}</span>}
      {expanded ? (
        <>
          <div class="speeds">
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                class={s === speed ? 'btn mono active' : 'btn mono'}
                aria-pressed={s === speed}
                onClick={() => {
                  clock.setSpeed(s);
                  setOpen(false);
                }}
              >
                {t('ui.life.speed', { n: s })}
              </button>
            ))}
          </div>
          <button
            type="button"
            class="btn ghost"
            onClick={() => {
              clock.skipToNextDecision();
              setOpen(false);
            }}
          >
            {t('ui.speed.skip')}
          </button>
        </>
      ) : (
        <button type="button" class="btn mono current" aria-expanded={false} onClick={() => setOpen(true)}>
          {t('ui.life.speed', { n: speed })}
        </button>
      )}
    </div>
  );
}
